import { FullCourse } from 'server/types';
import { AsyncForm, FormSubSection, InputLabel, Subtext } from '@components/async-form';
import { rpx } from 'client/lib/rpx-client';
import { BtnPrimary } from '@components/buttons';
import { showToast } from '@components/toaster';
import { HeadingSecondary } from '@course/components/headings';
import { useAsyncState } from 'client/lib/hooks';
import { Spinner } from '@components/spinner';
import { useState } from 'preact/hooks';

const store = rpx.courseSettings;

type Props = {
  course: FullCourse;
};

type OverrideStatus =
  | { kind: 'default' }
  | { kind: 'complete'; accountId: string }
  | { kind: 'partial'; missing: string[] };

function deriveOverrideStatus(values: {
  apiKey: string;
  appId: string;
  accountId: string;
  trackingDomain: string;
}): OverrideStatus {
  const present = [
    { name: 'Account ID', value: values.accountId },
    { name: 'Tracking Domain', value: values.trackingDomain },
    { name: 'API Key', value: values.apiKey },
    { name: 'App ID', value: values.appId },
  ];
  const filled = present.filter((f) => f.value.trim().length > 0);
  if (filled.length === 0) {
    return { kind: 'default' };
  }
  if (filled.length === 4) {
    return { kind: 'complete', accountId: values.accountId.trim() };
  }
  const missing = present.filter((f) => f.value.trim().length === 0).map((f) => f.name);
  return { kind: 'partial', missing };
}

export function IntegrationSettings({ course }: Props) {
  const [settings, setSettings] = useAsyncState(
    () => store.getCourseIntegrationSettings({ courseId: course.id }),
    [course.id],
  );

  const [productId, setProductId] = useState<string | undefined>(undefined);
  const [tagsText, setTagsText] = useState<string | undefined>(undefined);
  const [apiKey, setApiKey] = useState<string | undefined>(undefined);
  const [appId, setAppId] = useState<string | undefined>(undefined);
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const [trackingDomain, setTrackingDomain] = useState<string | undefined>(undefined);
  const [webhookProduct, setWebhookProduct] = useState<string | undefined>(undefined);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);

  if (!settings) {
    return (
      <div class="flex justify-center">
        <Spinner class="border-sky-500 w-8 h-8" />
      </div>
    );
  }

  const currentProductId = productId ?? settings.ontraportProductId;
  const currentTagsText = tagsText ?? settings.ontraportPurchaseTags.join('\n');
  const currentApiKey = apiKey ?? settings.ontraportApiKey;
  const currentAppId = appId ?? settings.ontraportAppId;
  const currentAccountId = accountId ?? settings.ontraportAccountId;
  const currentTrackingDomain = trackingDomain ?? settings.ontraportTrackingDomain;
  const currentWebhookProduct = webhookProduct ?? settings.ontraportWebhookProduct;

  const overrideStatus = deriveOverrideStatus({
    apiKey: currentApiKey,
    appId: currentAppId,
    accountId: currentAccountId,
    trackingDomain: currentTrackingDomain,
  });

  async function submit() {
    if (overrideStatus.kind === 'partial') {
      setValidationError(
        `Fill the missing ${overrideStatus.missing.join(', ')} to route this course to a different Ontraport account, or clear all four override fields to use the Mirasee default.`,
      );
      return;
    }
    setValidationError(undefined);

    const trimmedProductId = currentProductId.trim();
    const tags = currentTagsText
      .split('\n')
      .map((t) => t.trim())
      .filter(Boolean);
    try {
      await store.setCourseIntegrationSettings({
        courseId: course.id,
        ontraportProductId: trimmedProductId,
        ontraportPurchaseTags: tags,
        ontraportApiKey: currentApiKey.trim(),
        ontraportAppId: currentAppId.trim(),
        ontraportAccountId: currentAccountId.trim(),
        ontraportTrackingDomain: currentTrackingDomain.trim(),
        ...(settings?.canSetOntraportWebhookProduct
          ? { ontraportWebhookProduct: currentWebhookProduct.trim() }
          : {}),
      });
    } catch (err) {
      const message = extractValidationMessage(err);
      if (message) {
        setValidationError(message);
        return;
      }
      throw err;
    }

    const hasOverride = overrideStatus.kind === 'complete';
    setSettings({
      ontraportProductId: trimmedProductId,
      ontraportPurchaseTags: tags,
      ontraportApiKey: currentApiKey.trim().startsWith('****')
        ? currentApiKey.trim()
        : currentApiKey.trim()
          ? maskLocal(currentApiKey.trim())
          : '',
      ontraportAppId: currentAppId.trim(),
      ontraportAccountId: currentAccountId.trim(),
      ontraportTrackingDomain: currentTrackingDomain.trim(),
      ontraportWebhookProduct: currentWebhookProduct.trim(),
      canSetOntraportWebhookProduct: settings?.canSetOntraportWebhookProduct ?? false,
      ontraportConnected: settings?.ontraportConnected ?? false,
      ontraportSource: hasOverride ? 'course' : (settings?.ontraportSource ?? 'tenant'),
    });

    setProductId(undefined);
    setTagsText(undefined);
    setApiKey(undefined);
    setAppId(undefined);
    setAccountId(undefined);
    setTrackingDomain(undefined);
    setWebhookProduct(undefined);

    showToast({
      title: 'Settings saved',
      message: 'Integration settings have been updated.',
      type: 'ok',
    });
  }

  return (
    <AsyncForm class="flex flex-col gap-8" onSubmit={submit}>
      <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
        <HeadingSecondary>Ontraport Integration</HeadingSecondary>

        <FormSubSection>
          <InputLabel>Product ID</InputLabel>
          <Subtext>
            The numeric Ontraport product that purchases of this course are logged against, at the
            amount actually paid. Find it in Ontraport under Sales → Products. Leave blank and the
            first sale finds or creates a product named after this course and fills this in. Sales
            in another currency are logged against a product named like “Course (CAD)”, created
            automatically.
          </Subtext>
          <input
            class="ruz-input"
            type="text"
            value={currentProductId}
            placeholder="e.g. 12345"
            onInput={(e) => setProductId((e.target as HTMLInputElement).value)}
          />
        </FormSubSection>

        <FormSubSection>
          <InputLabel>Purchase Tags</InputLabel>
          <Subtext>
            Tags applied to the buyer's Ontraport contact on purchase. One tag per line. If empty,
            defaults to "Purchased - [Course Title]".
          </Subtext>
          <textarea
            class="ruz-input"
            rows={4}
            value={currentTagsText}
            placeholder={`e.g.\n[26-05] AIP Buyer (FP)\n[customer] AIP`}
            onInput={(e) => setTagsText((e.target as HTMLTextAreaElement).value)}
          />
        </FormSubSection>

        <FormSubSection>
          <InputLabel>Webhook product key</InputLabel>
          <Subtext>
            Buyers from an Ontraport order form are enrolled in this course when its webhook sends
            this product value (e.g. <code>ris-cohort-2</code>). One course per key. Leave blank if
            this course isn't sold through an Ontraport webhook.
          </Subtext>
          <input
            class="ruz-input"
            type="text"
            value={currentWebhookProduct}
            placeholder={settings.canSetOntraportWebhookProduct ? 'e.g. ris-cohort-2' : 'Not set'}
            disabled={!settings.canSetOntraportWebhookProduct}
            onInput={(e) => setWebhookProduct((e.target as HTMLInputElement).value)}
          />
          {!settings.canSetOntraportWebhookProduct && (
            <Subtext>Only a super admin can change this.</Subtext>
          )}
        </FormSubSection>

        <FormSubSection>
          <InputLabel>On Cancellation</InputLabel>
          <Subtext>
            When a subscription is canceled, the purchase tags above are automatically removed from
            the contact in Ontraport. This keeps your tagged lists up to date with active members
            only.
          </Subtext>
        </FormSubSection>
      </div>

      <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
        <div class="flex items-center justify-between gap-4 flex-wrap">
          <HeadingSecondary>Ontraport routing</HeadingSecondary>
          <RoutingBadge status={overrideStatus} />
        </div>

        <Subtext>
          By default, this course routes to the Mirasee Ontraport account. To route purchases to a
          different brand's account (e.g. Story Coach), enter all four fields below — they work as a
          unit. Partial config silently splits purchases between accounts.
        </Subtext>

        <FormSubSection>
          <InputLabel>Account ID</InputLabel>
          <Subtext>
            Numeric Ontraport account ID (e.g. <code>266096</code> for Story Coach). Drives the
            page-view tracker on campaign pages.
          </Subtext>
          <input
            class="ruz-input"
            type="text"
            value={currentAccountId}
            placeholder="Leave blank to use Mirasee"
            onInput={(e) => setAccountId((e.target as HTMLInputElement).value)}
          />
        </FormSubSection>

        <FormSubSection>
          <InputLabel>Tracking Domain</InputLabel>
          <Subtext>
            The brand's custom Ontraport tracking domain (e.g. <code>safe.story-coach.com</code>).
            The post-purchase order pixel posts here — if this doesn't match the account, sales
            appear in the wrong Ontraport.
          </Subtext>
          <input
            class="ruz-input"
            type="text"
            value={currentTrackingDomain}
            placeholder="Leave blank to use Mirasee"
            onInput={(e) => setTrackingDomain((e.target as HTMLInputElement).value)}
          />
        </FormSubSection>

        <FormSubSection>
          <InputLabel>API Key</InputLabel>
          <Subtext>
            The brand's Ontraport API key. Drives the contact + tag API calls on purchase. Stored
            encrypted; only the last 4 characters are shown after save.
          </Subtext>
          <input
            class="ruz-input"
            type="password"
            value={currentApiKey}
            placeholder="Leave blank to use Mirasee"
            onInput={(e) => setApiKey((e.target as HTMLInputElement).value)}
          />
        </FormSubSection>

        <FormSubSection>
          <InputLabel>App ID</InputLabel>
          <Subtext>The brand's Ontraport App ID, paired with the API key.</Subtext>
          <input
            class="ruz-input"
            type="text"
            value={currentAppId}
            placeholder="Leave blank to use Mirasee"
            onInput={(e) => setAppId((e.target as HTMLInputElement).value)}
          />
        </FormSubSection>

        {validationError && (
          <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {validationError}
          </div>
        )}
      </div>

      <footer>
        <BtnPrimary>Save</BtnPrimary>
      </footer>
    </AsyncForm>
  );
}

function RoutingBadge({ status }: { status: OverrideStatus }) {
  if (status.kind === 'default') {
    return (
      <span class="inline-flex items-center gap-1.5 text-xs font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2.5 py-1">
        <span class="w-1.5 h-1.5 rounded-full bg-sky-500" />
        Routing to Mirasee Ontraport
      </span>
    );
  }
  if (status.kind === 'complete') {
    return (
      <span class="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1">
        <span class="w-1.5 h-1.5 rounded-full bg-green-500" />
        Routing to Ontraport account {status.accountId}
      </span>
    );
  }
  return (
    <span class="inline-flex items-center gap-1.5 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-300 rounded-full px-2.5 py-1">
      <span class="w-1.5 h-1.5 rounded-full bg-amber-500" />
      Incomplete override — purchases will split between accounts
    </span>
  );
}

function maskLocal(key: string): string {
  if (key.length <= 4) {
    return '****';
  }
  return '****' + key.slice(-4);
}

function extractValidationMessage(err: unknown): string | undefined {
  const message = (err as { message?: unknown })?.message;
  if (
    typeof message === 'string' &&
    (message.includes('Ontraport account override is incomplete') ||
      message.includes('Webhook product key') ||
      message.includes('webhook product key'))
  ) {
    return message;
  }
  return undefined;
}
